import React, {useEffect, useState} from 'react';
import {AlertTriangle, Loader2, RefreshCw} from 'lucide-react';
import {toast} from 'react-toastify';
import {apiClient} from '@/shared/services/apiClient';

interface Props {
    orgId: number;
}

interface SyncSummary {
    usersProcessed: number;
    usersCreated: number;
    resultSetsCreated: number;
    resultSetsUpdated: number;
    usersWithNoResults: number;
    ordersRelinked: number;
    patientsReassigned: number;
    errors: string[];
}

type SyncJobStatus = 'pending' | 'running' | 'succeeded' | 'failed';

interface SyncJobRecord {
    id: string;
    status: SyncJobStatus;
    progress: {message?: string; updatedAt?: string};
    summary: SyncSummary | null;
    error: string | null;
}

interface ActiveProvider {
    id: number;
    name: string;
}

interface CurrentOhdProvider {
    provider_id: number;
    provider_name: string;
    patient_count: number;
}

const OhdSyncButton: React.FC<Props> = ({orgId}) => {
    const [syncing, setSyncing] = useState(false);
    const [providers, setProviders] = useState<ActiveProvider[] | null>(null);
    const [currentOhdProvider, setCurrentOhdProvider] = useState<CurrentOhdProvider | null>(null);
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
    const [showPicker, setShowPicker] = useState(false);

    useEffect(() => {
        apiClient
            .get<{providers: ActiveProvider[]; currentOhdProvider: CurrentOhdProvider | null}>(
                `/api/admin/ohd/active-providers/${orgId}`
            )
            .then((resp) => {
                setProviders(resp.providers);
                setCurrentOhdProvider(resp.currentOhdProvider);
                if (resp.providers.length === 1) {
                    setSelectedProviderId(resp.providers[0].id);
                }
            })
            .catch((err) => {
                // Previously this silently set providers to [], which left the
                // sync button disabled with no signal to the admin that the
                // provider list failed to load. Surface the failure so they
                // know to check the API or refresh.
                setProviders([]);
                toast.error(
                    `Failed to load active providers: ${err instanceof Error ? err.message : 'unknown error'}`,
                    {autoClose: 8000}
                );
            });
    }, [orgId]);

    function handleClick() {
        if (!providers) return;
        if (providers.length > 1) {
            setShowPicker(true);
            return;
        }
        runSync(selectedProviderId ?? providers[0]?.id);
    }

    function handlePickerConfirm() {
        if (!selectedProviderId) return;
        setShowPicker(false);
        runSync(selectedProviderId);
    }

    const isReassigning =
        selectedProviderId != null &&
        currentOhdProvider != null &&
        currentOhdProvider.patient_count > 0 &&
        selectedProviderId !== currentOhdProvider.provider_id;

    async function runSync(providerId: number | undefined) {
        setSyncing(true);

        // One persistent toast that we update as progress comes in, then
        // transform to success/warning/error at the end. Same channel as
        // every other notification in this component for visual consistency.
        const toastId = toast.loading('Starting OHD sync…');

        try {
            // BE returns 202 + {jobId} immediately. The actual sync runs in the
            // background; we poll for status until it's done. 409 means a
            // sync is already running for this org — we attach to the
            // existing job rather than rejecting.
            const startResp = await apiClient
                .post<{jobId: string; status: SyncJobStatus}>(`/api/admin/ohd/sync/${orgId}`, {providerId})
                .catch((err) => {
                    const conflict = err?.status === 409 && err?.data?.jobId;
                    if (conflict) {
                        return {jobId: err.data.jobId as string, status: 'running' as SyncJobStatus};
                    }
                    throw err;
                });

            const finalJob = await pollUntilDone(startResp.jobId, toastId);

            if (finalJob.status === 'failed') {
                toast.update(toastId, {
                    render: `Sync failed: ${finalJob.error ?? 'unknown error'}`,
                    type: 'error',
                    isLoading: false,
                    autoClose: 10000,
                });
                return;
            }

            const s = finalJob.summary;
            if (!s) {
                toast.update(toastId, {
                    render: 'Sync finished but returned no summary.',
                    type: 'warning',
                    isLoading: false,
                    autoClose: 10000,
                });
                return;
            }

            const parts = [`${s.resultSetsCreated} new result sets`, `${s.resultSetsUpdated} reconciled`];
            if (s.usersCreated > 0) parts.push(`${s.usersCreated} users created`);
            if (s.usersWithNoResults > 0) parts.push(`${s.usersWithNoResults} with no results yet`);
            if (s.ordersRelinked > 0) parts.push(`${s.ordersRelinked} orders relinked`);
            if (s.patientsReassigned > 0) parts.push(`${s.patientsReassigned} patients reassigned`);
            if (s.errors.length > 0) parts.push(`${s.errors.length} errors`);

            toast.update(toastId, {
                render:
                    s.errors.length > 0
                        ? `Sync completed with issues: ${parts.join(', ')}`
                        : `Sync complete: ${parts.join(', ')}`,
                type: s.errors.length > 0 ? 'warning' : 'success',
                isLoading: false,
                autoClose: 10000,
            });

            setCurrentOhdProvider(
                providerId
                    ? {
                          provider_id: providerId,
                          provider_name: providers?.find((p) => p.id === providerId)?.name ?? '',
                          patient_count: s.usersProcessed,
                      }
                    : currentOhdProvider
            );
        } catch (err) {
            // Distinguish "FE gave up watching" from "sync actually failed".
            // The first should not say "Sync failed" — the BE worker is
            // likely still making progress.
            const stillRunning = (err as Error & {syncMayStillBeRunning?: boolean})?.syncMayStillBeRunning;
            toast.update(toastId, {
                render: stillRunning
                    ? (err as Error).message
                    : `Sync failed: ${err instanceof Error ? err.message : 'unknown error'}`,
                type: stillRunning ? 'warning' : 'error',
                isLoading: false,
                autoClose: 10000,
            });
        } finally {
            setSyncing(false);
        }
    }

    // Poll the job until it reaches a terminal state. 2.5s cadence is a
    // compromise between responsiveness and load — at scale this is one
    // admin clicking sync, not a hot path. The latest progress message is
    // pushed into the in-flight toast so the admin sees live status.
    //
    // Transient poll failures (network blip, cold-start window after a pm2
    // reload, brief CDN hiccup) don't fail the sync — the BE job keeps
    // running regardless of whether the FE is watching. We log, surface a
    // gentle "reconnecting" message in the toast, and try again next tick.
    // Only after several consecutive failures do we give up; the BE state
    // can still be inspected via the job id in dev tools.
    async function pollUntilDone(jobId: string, toastId: ReturnType<typeof toast.loading>): Promise<SyncJobRecord> {
        const POLL_INTERVAL_MS = 2_500;
        // Hard ceiling on FE-side polling. With the OHD rate limiter at
        // 5 req/sec, a full 19k-user sync legitimately takes ~65 minutes;
        // 2 hours leaves headroom for slower upstreams and re-runs that
        // have lots of skipped result sets to scan. The BE worker keeps
        // running regardless of whether we're watching, so this isn't a
        // sync timeout — it's only how long the FE keeps a toast open.
        const MAX_WAIT_MS = 2 * 60 * 60 * 1000;
        const MAX_CONSECUTIVE_POLL_FAILURES = 10; // ~25s of failed polls before we give up
        // Per-poll timeout. Bigger than apiClient's default 30s because we've
        // seen browser-level net::ERR_TIMED_OUT on this endpoint when the
        // API was under load — giving each poll a generous budget keeps
        // the watch from giving up over a single slow response.
        const POLL_REQUEST_TIMEOUT_MS = 60_000;
        const startedAt = Date.now();
        let consecutiveFailures = 0;

        while (Date.now() - startedAt < MAX_WAIT_MS) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

            let job: SyncJobRecord;
            try {
                job = await apiClient.get<SyncJobRecord>(`/api/admin/ohd/sync/jobs/${jobId}`, {
                    timeout: POLL_REQUEST_TIMEOUT_MS,
                });
                consecutiveFailures = 0;
            } catch (err) {
                consecutiveFailures++;
                if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
                    throw new Error(
                        `Lost connection while watching sync (job ${jobId}). The sync may still be running on the server.`
                    );
                }
                toast.update(toastId, {
                    render: `Reconnecting to sync… (${err instanceof Error ? err.message : 'network error'})`,
                });
                continue;
            }

            if (job.progress?.message) {
                toast.update(toastId, {render: job.progress.message});
            }
            if (job.status === 'succeeded' || job.status === 'failed') return job;
        }
        // Reaching this means we hit the FE polling ceiling — NOT necessarily
        // a sync failure. The BE worker may still be making progress; the
        // job state can be inspected by re-clicking Sync (the 409+attach
        // path will reconnect to it).
        const err = new Error(
            `Stopped watching sync after ${Math.round(MAX_WAIT_MS / 60000)} min. Job ${jobId} may still be running — click Sync to re-attach, or check the admin OHD logs.`
        );
        (err as Error & {syncMayStillBeRunning?: boolean}).syncMayStillBeRunning = true;
        throw err;
    }

    return (
        <div className="relative ml-2 flex items-center gap-2">
            <button
                type="button"
                disabled={syncing || !providers}
                onClick={handleClick}
                className="inline-flex items-center gap-1.5 rounded-md border border-mld-panel-border bg-transparent px-2.5 py-1 text-[11px] font-semibold text-mld-menu-text hover:bg-mld-menu-background hover:border-mld-primary disabled:opacity-60">
                {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {syncing ? 'Syncing…' : 'Sync Patients'}
            </button>

            {showPicker && providers && providers.length > 1 && (
                <div className="absolute top-full left-0 z-50 mt-1 w-72 rounded-lg border border-mld-panel-border bg-white p-3 shadow-xl">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-mld-muted-text">
                        Select Provider
                    </div>
                    <select
                        className="mb-2 w-full rounded-md border border-gray-300 bg-white pl-2 pr-8 py-1.5 text-xs text-mld-menu-text"
                        value={selectedProviderId ?? ''}
                        onChange={(e) => setSelectedProviderId(Number(e.target.value) || null)}>
                        <option value="">Choose a provider…</option>
                        {providers.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>

                    {isReassigning && (
                        <div className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
                            <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0 text-amber-600" />
                            <p className="text-[11px] leading-relaxed text-amber-800">
                                {currentOhdProvider.patient_count.toLocaleString()} OHD patients are currently linked to{' '}
                                <strong>{currentOhdProvider.provider_name}</strong>. They will be moved to{' '}
                                <strong>{providers.find((p) => p.id === selectedProviderId)?.name}</strong>.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setShowPicker(false)}
                            className="rounded px-2 py-1 text-[11px] text-mld-muted-text hover:bg-mld-menu-background">
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!selectedProviderId}
                            onClick={handlePickerConfirm}
                            className="rounded bg-mld-menu-text px-3 py-1 text-[11px] font-semibold text-white hover:bg-mld-menu-text/90 disabled:opacity-50">
                            {isReassigning ? 'Move & Sync' : 'Sync'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OhdSyncButton;
